import { useState, useEffect } from "react";
import { toast } from "sonner";
import { User } from "lucide-react";
import api, { apiErr } from "@/lib/api";
import { Dialog, DialogTrigger, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AccountRow } from "./AccountRow";

export const CATS = [["professional", "Working Professional"], ["student", "Student"], ["trader", "Trader / Investor"]];
export const catLabel = (v) => CATS.find((c) => c[0] === v)?.[1] || "Not set";

export function EditProfileDialog({ user, onDone }) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState(user?.name || "");
  const [category, setCategory] = useState(user?.category || "professional");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) { setName(user?.name || ""); setCategory(user?.category || "professional"); }
  }, [open]);

  const save = async () => {
    setSaving(true);
    try {
      await api.patch("/me/profile", { name, category });
      toast.success("Profile updated");
      await onDone?.();
      setOpen(false);
    } catch (e) { toast.error(apiErr(e)); } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button className="w-full" data-testid="open-profile-dialog">
          <AccountRow icon={User} title="Name & Category" value={`${user?.name || "—"} · ${catLabel(user?.category)}`} testid="account-row-profile" />
        </button>
      </DialogTrigger>
      <DialogContent data-testid="edit-profile-dialog">
        <DialogHeader><DialogTitle>Name & Category</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label>Full name</Label>
            <Input data-testid="profile-name-input" value={name} onChange={(e) => setName(e.target.value)} />
          </div>
          <div className="space-y-1.5">
            <Label>I am a</Label>
            <Select value={category} onValueChange={setCategory}>
              <SelectTrigger data-testid="profile-category-trigger"><SelectValue /></SelectTrigger>
              <SelectContent>{CATS.map((c) => <SelectItem key={c[0]} value={c[0]}>{c[1]}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        </div>
        <DialogFooter>
          <button data-testid="profile-save-btn" onClick={save} disabled={saving}
            className="h-10 px-5 rounded-md bg-navy text-white text-sm font-semibold hover:bg-navy-light transition-colors disabled:opacity-60">
            {saving ? "Saving…" : "Save changes"}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
